"use client";

import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/app/components/ui/card";

export default function UUIDInformation() {
  return (
    <Card className="card-enhanced">
      <CardHeader>
        <CardTitle>UUID Version Information</CardTitle>
        <CardDescription>
          Learn about different UUID versions and their use cases
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="space-y-2">
            <h3 className="font-semibold">UUID v1 (Time-based)</h3>
            <p className="text-sm text-muted-foreground">
              Based on timestamp and MAC address. Good for chronological ordering but may reveal device information.
            </p>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• Time-based generation</li>
              <li>• Includes timestamp</li>
              <li>• Good for ordering</li>
              <li>• May reveal device info</li>
            </ul>
          </div>
          <div className="space-y-2">
            <h3 className="font-semibold">UUID v3 (MD5 based)</h3>
            <p className="text-sm text-muted-foreground">
              Generated using MD5 hash of a namespace UUID and name. Deprecated due to MD5 vulnerabilities.
            </p>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• Deterministic generation</li>
              <li>• Based on namespace + name</li>
              <li>• MD5 hash algorithm</li>
              <li>• Deprecated (use v5 instead)</li>
            </ul>
          </div>
          <div className="space-y-2">
            <h3 className="font-semibold">UUID v4 (Random)</h3>
            <p className="text-sm text-muted-foreground">
              Generated using random or pseudo-random numbers. Most commonly used and recommended for general purposes.
            </p>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• Cryptographically secure</li>
              <li>• Completely random</li>
              <li>• No predictable pattern</li>
              <li>• Most widely used</li>
            </ul>
          </div>
          <div className="space-y-2">
            <h3 className="font-semibold">UUID v5 (SHA-1 based)</h3>
            <p className="text-sm text-muted-foreground">
              Generated using SHA-1 hash of a namespace UUID and name. Deterministic for the same inputs.
            </p>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• Deterministic generation</li>
              <li>• Based on namespace + name</li>
              <li>• SHA-1 hash algorithm</li>
              <li>• Recommended over v3</li>
            </ul>
          </div>
          <div className="space-y-2">
            <h3 className="font-semibold">UUID v6 (Reordered timestamp)</h3>
            <p className="text-sm text-muted-foreground">
              Similar to v1 but with reordered timestamp fields for better sorting performance.
            </p>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• Time-based generation</li>
              <li>• Better sorting than v1</li>
              <li>• Modern alternative to v1</li>
              <li>• Good for databases</li>
            </ul>
          </div>
          <div className="space-y-2">
            <h3 className="font-semibold">UUID v7 (Unix-time ordered)</h3>
            <p className="text-sm text-muted-foreground">
              Uses Unix timestamp with additional randomness. Recommended for new applications.
            </p>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• Unix timestamp based</li>
              <li>• Excellent sorting</li>
              <li>• Recommended for new apps</li>
              <li>• Future-proof design</li>
            </ul>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
